import { z } from 'zod';
import buchRoh from './datenregel-vorlagen-fassungen.json' with { type: 'json' };
import katalogRoh from './datenregel-vorlagen.json' with { type: 'json' };
import { datenregelHerkunftVorlageSchema, datenregelKatalogSchema, istPlatzhalterKriterium, vorlagenHash, } from './datenregel-satz.js';
const fassungsbuchSchema = z
    .object({
    _hinweis: z.string(),
    fassungen: z.array(datenregelHerkunftVorlageSchema),
})
    .strict();
function zodBefunde(art, fehler) {
    return fehler.issues.map((befund) => ({
        art,
        text: `${befund.path.join('.') || '(Wurzel)'}: ${befund.message}`,
    }));
}
/** Vergleichsform eines Titels — so, wie zwei Regelnamen kollidieren würden. */
function titelSchluessel(titel) {
    return titel.toLocaleLowerCase('de-DE');
}
function pruefeVorlagen(vorlagen) {
    const befunde = [];
    const kennungen = new Set();
    const titel = new Map();
    for (const vorlage of vorlagen) {
        if (kennungen.has(vorlage.kennung)) {
            befunde.push({
                art: 'kennung_doppelt',
                kennung: vorlage.kennung,
                text: `Die Kennung ${vorlage.kennung} steht mehrfach im Katalog`,
            });
        }
        kennungen.add(vorlage.kennung);
        const schluessel = titelSchluessel(vorlage.titel);
        const vorher = titel.get(schluessel);
        if (vorher !== undefined) {
            befunde.push({
                art: 'titel_doppelt',
                kennung: vorlage.kennung,
                text: `${vorlage.kennung} trägt denselben Titel wie ${vorher} („${vorlage.titel}“)`,
            });
        }
        else {
            titel.set(schluessel, vorlage.kennung);
        }
        if (vorlage.beschreibung.length === 0) {
            befunde.push({
                art: 'beschreibung_leer',
                kennung: vorlage.kennung,
                text: `${vorlage.kennung} hat keine Beschreibung`,
            });
        }
        const dienst = vorlage.kriterien.dienst;
        const dienstFest = dienst !== undefined && !istPlatzhalterKriterium(dienst) ? dienst.wert : undefined;
        if (dienstFest !== undefined && dienstFest !== 'fints') {
            befunde.push({
                art: 'dienst_nicht_bank',
                kennung: vorlage.kennung,
                text: `${vorlage.kennung} nennt fest den Dienst ${dienstFest}; dort bucht eine Regel im Code`,
            });
        }
        if (vorlage.klasse === 'zahlungsdienst' && dienstFest !== 'fints') {
            befunde.push({
                art: 'zahlungsdienst_ohne_fints',
                kennung: vorlage.kennung,
                text: `Die Zahlungsdienst-Vorlage ${vorlage.kennung} trägt nicht fest dienst = fints`,
            });
        }
    }
    return befunde;
}
function pruefeBuch(vorlagen, fassungen) {
    const befunde = [];
    const zeilen = new Map();
    for (const zeile of fassungen) {
        const jeKennung = zeilen.get(zeile.kennung) ?? new Map();
        if (jeKennung.has(zeile.fassung)) {
            befunde.push({
                art: 'buch_doppelt',
                kennung: zeile.kennung,
                fassung: zeile.fassung,
                text: `Das Fassungsbuch führt ${zeile.kennung} Fassung ${zeile.fassung} mehrfach`,
            });
        }
        jeKennung.set(zeile.fassung, zeile.sha256);
        zeilen.set(zeile.kennung, jeKennung);
    }
    for (const [kennung, jeKennung] of zeilen) {
        const hoechste = Math.max(...jeKennung.keys());
        for (let fassung = 1; fassung <= hoechste; fassung += 1) {
            if (!jeKennung.has(fassung)) {
                befunde.push({
                    art: 'buch_luecke',
                    kennung,
                    fassung,
                    text: `Im Fassungsbuch fehlt ${kennung} Fassung ${fassung} — das Buch wächst nur`,
                });
            }
        }
    }
    for (const vorlage of vorlagen) {
        const jeKennung = zeilen.get(vorlage.kennung);
        const sha256 = vorlagenHash(vorlage);
        const gebucht = jeKennung?.get(vorlage.fassung);
        if (gebucht === undefined) {
            befunde.push({
                art: 'fassung_unbelegt',
                kennung: vorlage.kennung,
                fassung: vorlage.fassung,
                text: `${vorlage.kennung} Fassung ${vorlage.fassung} steht nicht im Fassungsbuch (SHA-256 ${sha256})`,
            });
        }
        else if (gebucht !== sha256) {
            befunde.push({
                art: 'hash_abweichend',
                kennung: vorlage.kennung,
                fassung: vorlage.fassung,
                text: `${vorlage.kennung} Fassung ${vorlage.fassung} weicht vom Fassungsbuch ab ` +
                    `(gebucht ${gebucht}, Inhalt ${sha256}) — eine geänderte Vorlage braucht eine neue Fassung`,
            });
        }
        if (jeKennung !== undefined) {
            const hoechste = Math.max(...jeKennung.keys());
            if (hoechste > vorlage.fassung) {
                befunde.push({
                    art: 'buch_voraus',
                    kennung: vorlage.kennung,
                    fassung: hoechste,
                    text: `Das Fassungsbuch führt ${vorlage.kennung} Fassung ${hoechste}, der Katalog Fassung ${vorlage.fassung}`,
                });
            }
        }
    }
    return befunde;
}
/**
 * **Die Katalogprüfung**: Vertrag (`datenregelKatalogSchema`), eindeutige
 * Kennungen und Titel, eine Beschreibung je Vorlage, Zahlungsdienst-Vorlagen
 * fest auf `dienst = fints` und kein fester anderer Dienst (`D-40`), und das
 * Fassungsbuch: je Kennung und Fassung genau eine Zeile, lückenlos ab 1, der
 * Hash der aktuellen Fassung gleich dem gebuchten, keine Buchzeile über der
 * Katalogfassung.
 *
 * Rein, ohne Datenbank und ohne Ausnahme: Sie liefert Befunde, sie wirft nicht.
 * Ein Befund nennt den Hash, den die Buchzeile tragen müsste — wer eine Vorlage
 * ändert, erhöht ihre Fassung und trägt diesen Hash als neue Zeile ein.
 */
export function pruefeVorlagenkatalog(katalogEingabe, buchEingabe) {
    const katalog = datenregelKatalogSchema.safeParse(katalogEingabe);
    const buch = fassungsbuchSchema.safeParse(buchEingabe);
    const befunde = [];
    if (!katalog.success)
        befunde.push(...zodBefunde('vertrag', katalog.error));
    if (!buch.success)
        befunde.push(...zodBefunde('buch_form', buch.error));
    if (katalog.success)
        befunde.push(...pruefeVorlagen(katalog.data.regeln));
    if (katalog.success && buch.success)
        befunde.push(...pruefeBuch(katalog.data.regeln, buch.data.fassungen));
    return {
        ...(katalog.success ? { katalog: katalog.data } : {}),
        ...(buch.success ? { fassungen: buch.data.fassungen } : {}),
        befunde,
    };
}
function ladeKatalog() {
    const pruefung = pruefeVorlagenkatalog(katalogRoh, buchRoh);
    if (pruefung.befunde.length > 0 || pruefung.katalog === undefined || pruefung.fassungen === undefined) {
        const zeilen = pruefung.befunde.map((befund) => `  - [${befund.art}] ${befund.text}`).join('\n');
        throw new Error(`Der Vorlagenkatalog der Datenregeln ist ungültig:\n${zeilen}`);
    }
    return { vorlagen: pruefung.katalog.regeln, fassungen: pruefung.fassungen };
}
const geladen = ladeKatalog();
/** Die Vorlagen des Katalogs in ihrer aktuellen Fassung, in der Reihenfolge der Datei. */
export const DATENREGEL_VORLAGEN = geladen.vorlagen;
/** Das Fassungsbuch: jede je ausgelieferte Vorlagenfassung mit ihrem SHA-256. */
export const DATENREGEL_VORLAGEN_FASSUNGSBUCH = geladen.fassungen;
/** Die Vorlage mit dieser Kennung in ihrer aktuellen Fassung — oder `undefined`. */
export function vorlageNach(kennung) {
    return DATENREGEL_VORLAGEN.find((vorlage) => vorlage.kennung === kennung);
}
/**
 * Ob das **eigene** Fassungsbuch eine Herkunft bestätigt — Kennung, Fassung und
 * SHA-256 stehen dort genau so. Der Import übernimmt eine Vorlagenherkunft nur
 * dann (Spec 17.5): Die Datei behauptet, das Buch belegt.
 */
export function fassungsbuchBestaetigt(herkunft) {
    return DATENREGEL_VORLAGEN_FASSUNGSBUCH.some((zeile) => zeile.kennung === herkunft.kennung &&
        zeile.fassung === herkunft.fassung &&
        zeile.sha256 === herkunft.sha256);
}
//# sourceMappingURL=datenregel-vorlagen.js.map